import { memo } from "react";
import PropTypes from "prop-types";
import get from "lodash/get";
import { FormattedMessage, FormattedPlural } from "react-intl";
import { FLIGHT_OPTIONS_STATUS, TRANSPORTATION_TYPES } from "app/constants";
import "./LuggageSummary.scss";
import LuggagePlaceholderLoader from "app/pages/Booking/Quotation/Luggage/LuggagePlaceholderLoader/LuggagePlaceholderLoader";
import classNames from "classnames";
import IconHandLuggageAvailable from "app/pages/.shared/IconHandLuggageAvailable";
import IconCarryOnLuggageAvailable from "app/pages/.shared/static/icons/IconCarryOnLuggageAvailable";
import IconCheckInLuggageUnavailable from "app/pages/.shared/static/icons/IconCheckInLuggageUnavailable";
import IconCheckInLuggageAvailable from "app/pages/.shared/static/icons/IconCheckInLuggageAvailable";
import IconHandLuggageUnavailable from "app/pages/.shared/IconHandLuggageUnavailable";
import IconCarryOnLuggageUnavailable from "app/pages/.shared/static/icons/IconCarryOnLuggageUnavailable";
import { getCabinBaggageCount } from "app/pages/Booking/Quotation/luggageHelpers";
import LuggageSummaryAddButton from "app/pages/Booking/Quotation/Flight/LuggageSummary/LuggageSummaryAddButton";

const LuggageSummary = ({
	flight = {},
	flightOptions = {},
	bookingLuggageCount,
	bookingCabinLuggage,
	luggageMaxCount,
	transportationType,
	onLuggageClick = () => {},
	hideAddLuggageButton,
	flightOptionsStatus,
	maxWeightPerCheckedBaggage = 0,
}) => {
	const isTravelByTrain = transportationType === TRANSPORTATION_TYPES.TRAIN;
	const luggage = flightOptions?.flightBagageOption || flight?.flightOptions?.flightBagageOption;
	const { cabinBaggage, quantity: totalLuggageCount } = luggage || {};

	const isBaggageFree = flightOptions?.flightBagageOptions?.[0].pricePerBooking === 0;

	let luggageCount = 0;

	if (bookingLuggageCount) {
		luggageCount = bookingLuggageCount;
	} else if (totalLuggageCount) {
		luggageCount = totalLuggageCount;
	} else if (get(luggage, "included")) {
		const luggageMaxCountPerPassenger = Math.floor(totalLuggageCount / luggageMaxCount);
		luggageCount = totalLuggageCount ? luggageMaxCountPerPassenger : 1;
	} else if (isBaggageFree) {
		luggageCount = luggageMaxCount;
	} else if (get(luggage, "canBeBooked")) {
		luggageCount = 0;
	}

	const isBaggageNotBookable =
		get(luggage, "included") === false && get(luggage, "canBeBooked") === false;

	const isBaggageBookable = luggage?.canBeBooked && !luggage?.included;

	const cabinBaggageCount = getCabinBaggageCount(
		cabinBaggage,
		bookingCabinLuggage?.quantity,
		luggageMaxCount
	);

	const isLoading = flightOptionsStatus === FLIGHT_OPTIONS_STATUS.LOADING;

	if (isLoading) {
		return (
			<div className="luggage-summary__placeholder-loader">
				<LuggagePlaceholderLoader />
			</div>
		);
	}

	let luggageDisplayValue = "";

	const handLuggageDetails = luggage?.handBaggage?.details;
	const isHandBaggageIncluded = luggage?.handBaggage?.included;

	if (isBaggageNotBookable) {
		luggageDisplayValue = (
			<>
				: <FormattedMessage id="quotation.luggage.not.available" />
			</>
		);
	} else if (!hideAddLuggageButton && isBaggageBookable) {
		luggageDisplayValue = <LuggageSummaryAddButton onLuggageClick={onLuggageClick} />;
	}

	const getLuggageClassNames = (count, maxCount, isIncluded) => {
		return {
			itemClassName: classNames("luggage-summary--item", {
				"luggage-summary--item-not-included": count === 0 || isIncluded === false,
			}),
			labelClassName: classNames("luggage-summary__label", {
				"luggage-summary__label--included": isIncluded,
			}),
			countClassName: classNames("luggage-summary__count", {
				"luggage-summary__count--included": count > 0 || maxCount > 0,
			}),
		};
	};

	const handLuggageClassNames = getLuggageClassNames(
		luggageMaxCount,
		luggageMaxCount,
		luggageMaxCount > 0
	);
	const cabinLuggageClassNames = getLuggageClassNames(
		luggageMaxCount,
		luggageMaxCount,
		cabinBaggageCount > 0
	);
	const holdLuggageClassNames = getLuggageClassNames(
		luggageCount,
		luggageMaxCount,
		luggageCount > 0
	);

	return (
		<div className="luggage-summary">
			{/* BAGAGE A MAIN */}

			{isHandBaggageIncluded && (
				<div className={handLuggageClassNames.itemClassName}>
					<span className="luggage-summary__icon">
						{luggageMaxCount > 0 ? (
							<IconHandLuggageAvailable />
						) : (
							<IconHandLuggageUnavailable />
						)}
					</span>
					<span
						className={handLuggageClassNames.labelClassName}
						data-testid="hand-luggage-summary-label"
					>
						{luggageMaxCount >= 0 && (
							<span className={handLuggageClassNames.countClassName}>
								{luggageMaxCount}x
							</span>
						)}

						<FormattedPlural
							value={luggageMaxCount}
							one={<FormattedMessage id="quotation.luggage.handbag.singular.label" />}
							other={<FormattedMessage id="quotation.luggage.handbag.plural.label" />}
						/>
						{handLuggageDetails && <> ({handLuggageDetails})</>}
					</span>
				</div>
			)}

			{/* BAGAGE EN CABINE */}
			<div className={cabinLuggageClassNames.itemClassName}>
				<span className="luggage-summary__icon">
					{cabinBaggageCount <= 0 ? (
						<IconCarryOnLuggageUnavailable />
					) : (
						<IconCarryOnLuggageAvailable />
					)}
				</span>
				<span
					className={cabinLuggageClassNames.labelClassName}
					data-testid="cabin-luggage-summary-label"
				>
					{cabinBaggageCount >= 0 && (
						<span className={cabinLuggageClassNames.countClassName}>
							{cabinBaggageCount}x
						</span>
					)}
					{isTravelByTrain ? (
						<FormattedMessage id="quotation.transport.luggage.small.plural" />
					) : (
						<FormattedPlural
							value={cabinBaggageCount}
							one={<FormattedMessage id="quotation.luggage.cabin.singular.label" />}
							other={<FormattedMessage id="quotation.luggage.cabin.plural.label" />}
						/>
					)}{" "}
					{!cabinBaggage?.included && !cabinBaggage?.canBeBooked ? (
						<>
							{/* TODO Temporarily hide message to launch asap release bagage cabine. Reable when we are able to fetch updated baggage options */}
							{/*: <FormattedMessage id="quotation.luggage.cabin.description.message" />*/}
						</>
					) : (
						<>
							{cabinBaggageCount > 0 && (
								<span>
									{cabinBaggage?.details && <> ({cabinBaggage?.details})</>}
								</span>
							)}
							{!hideAddLuggageButton &&
								cabinBaggage?.canBeBooked &&
								!cabinBaggage?.included && (
									<LuggageSummaryAddButton onLuggageClick={onLuggageClick} />
								)}
						</>
					)}
				</span>
			</div>
			{/* BAGAGE EN SOUTE */}
			<div className={holdLuggageClassNames.itemClassName}>
				<span className="luggage-summary__icon">
					{luggageCount > 0 || isBaggageFree ? (
						<IconCheckInLuggageAvailable />
					) : (
						<IconCheckInLuggageUnavailable />
					)}
				</span>
				<span
					className={holdLuggageClassNames.labelClassName}
					data-testid="checked-luggage-summary-label"
				>
					<span className={holdLuggageClassNames.countClassName}>{luggageCount}x</span>
					{isTravelByTrain ? (
						<FormattedPlural
							value={luggageCount}
							one={<FormattedMessage id="quotation.transport.luggage.regular" />}
							other={
								<FormattedMessage id="quotation.transport.luggage.regular.pluralized" />
							}
						/>
					) : (
						<FormattedPlural
							value={luggageCount}
							one={<FormattedMessage id="quotation.luggage.singular.label" />}
							other={<FormattedMessage id="quotation.luggage.plural.label" />}
						/>
					)}{" "}
					{maxWeightPerCheckedBaggage > 0 && luggageCount > 0 && (
						<span data-testid="max-weight-per-checked-baggage">
							(
							<FormattedMessage
								id="quotation.transport.luggage.option.max.weight"
								values={{
									weight: maxWeightPerCheckedBaggage,
								}}
							/>
							)
						</span>
					)}
					{luggageDisplayValue}
				</span>
			</div>
		</div>
	);
};

LuggageSummary.propTypes = {
	transportationType: PropTypes.oneOf([TRANSPORTATION_TYPES.FLIGHT, TRANSPORTATION_TYPES.TRAIN]),
	onLuggageClick: PropTypes.func,
	flight: PropTypes.object,
	bookingLuggageCount: PropTypes.number,
	bookingCabinLuggage: PropTypes.object,
	luggageMaxCount: PropTypes.number,
	hideAddLuggageButton: PropTypes.bool,
	flightOptions: PropTypes.object,
	flightOptionsStatus: PropTypes.oneOf([
		FLIGHT_OPTIONS_STATUS.LOADING,
		FLIGHT_OPTIONS_STATUS.NO_BAGAGE,
		FLIGHT_OPTIONS_STATUS.SUCCESS,
	]),
	maxWeightPerCheckedBaggage: PropTypes.number,
};

export default memo(LuggageSummary);
